import React, { useState, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useDisclosure,
  Box,
  useToast,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Icon,
  ModalFooter,
  Select,
  Text,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { FaEdit, FaTrash, FaTags } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const CategoryViewTable = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [filterTitle, setFilterTitle] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const schoolId = sessionStorage.getItem("school_id");
        const response = await axios.get(
          `${BASE_URL}/admin/events/category/list_category.php?school_id=${schoolId}`
        );

        if (response.data.success) {
          setCategories(response.data.data);
          setFilteredCategories(response.data.data);

          const eventTitles = Array.from(
            new Set(response.data.data.map((category) => category.event_title))
          );
          setEvents(eventTitles);
        } else {
          alert(`Error: ${response.data.message}`);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        alert("An error occurred while fetching categories.");
      }
    };

    fetchData();
  }, []);

  const handleDelete = async (id) => {
    try {
      const schoolId = sessionStorage.getItem("school_id");
      const userId = sessionStorage.getItem("user_id");

      const data = { category_id: id, school_id: schoolId, user_id: userId };
      const response = await axios.delete(
        `${BASE_URL}/admin/events/category/delete_category.php`,
        { data }
      );

      if (response.data.success) {
        setCategories(categories.filter((category) => category.id !== id));
        setFilteredCategories(
          filteredCategories.filter((category) => category.id !== id)
        );
        toast({
          title: "Category Deleted",
          description: "Category deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error Deleting Category",
          description: "An error occurred while deleting the category.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error Deleting Category",
        description: "An error occurred while deleting the category.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateCategory = async () => {
    try {
      const schoolId = sessionStorage.getItem("school_id");
      const userId = sessionStorage.getItem("user_id");

      const data = {
        user_id: userId,
        school_id: schoolId,
        category_id: selectedCategory.id,
        event_id: selectedCategory.event_id,
        category: categoryName,
      };

      const response = await axios.put(
        `${BASE_URL}/admin/events/category/edit_category.php`,
        data
      );

      if (response.data.success) {
        setCategories(
          categories.map((cat) =>
            cat.id === selectedCategory.id
              ? { ...cat, category: categoryName }
              : cat
          )
        );
        setFilteredCategories(
          filteredCategories.map((cat) =>
            cat.id === selectedCategory.id
              ? { ...cat, category: categoryName }
              : cat
          )
        );
        toast({
          title: "Success",
          description: "Category updated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update category",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/events/category/view_category.php?category_id=${id}`
      );

      if (response.data.success) {
        const category = response.data.data;
        setSelectedCategory(category);
        setCategoryName(category.category);
        setCategoryDescription(category.description);
        onOpen();
      } else {
        toast({
          title: "Error fetching category details.",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
      toast({
        title: "An error occurred while fetching category details.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFilterChange = (e) => {
    const filter = e.target.value.toLowerCase();
    setSearchTerm(filter);
    setFilterTitle(filter);
    if (filter) {
      setFilteredCategories(
        categories.filter((category) =>
          category.event_title.toLowerCase().includes(filter)
        )
      );
    } else {
      setFilteredCategories(categories);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredCategories.length / rowsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const paginatedCategories = filteredCategories.slice(
    indexOfFirstRow,
    indexOfLastRow
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <Box p={4}>
      {/* Search Section */}
      <Flex
        direction={{ base: "column", md: "row" }} // Column on mobile, row on larger screens
        justify="space-between"
        align="center"
        mb={{ base: 4, md: 1 }} // Margin bottom for mobile view, reduced for desktop
        p={3}
        className="sb"
      >
        <Stack
          direction={{ base: "column", md: "row" }} // Stack vertically on mobile, horizontally on larger screens
          spacing={4}
          width="100%" // Full width to ensure responsiveness
          justify="space-between" // Align elements across space
        >
          <Flex align="center" width="auto">
            <Text mr={2}>Rows Per Page</Text>
            <Select
              width={{ base: "190px", md: "100px" }} // Adjust width for mobile and desktop
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              borderColor="#E9EDF4"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Select>
          </Flex>

          <Flex align="center" width="auto">
            <Input
              placeholder="Search by Event Title"
              value={searchTerm}
              onChange={handleFilterChange}
              width={{ base: "100%", md: "200px" }} // Adjust width for mobile and desktop
              mt={{ base: 2, md: 0 }} // Ensure top margin on mobile for better spacing
            />
          </Flex>
        </Stack>
      </Flex>

      <Box overflowX="auto" maxWidth="100%">
        {/* Table for displaying categories */}
        <Table
          sx={{
            "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
          }}
        >
          <Thead>
            <Tr>
              <Th>Category</Th>
              <Th>Event Title</Th>
              <Th textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedCategories.map((category) => (
              <Tr key={category.id}>
                <Td>{category.category}</Td>
                <Td>{category.event_title}</Td>
                <Td textAlign="right">
                  <Button
                    leftIcon={<FaEdit />}
                    colorScheme="blue"
                    size="sm"
                    mr={2}
                    onClick={() => handleEdit(category.id)}
                  ></Button>
                  <Button
                    leftIcon={<FaTrash />}
                    colorScheme="red"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                  ></Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      <Flex justify="end" borderTopWidth="1px" mt={3} p={3} className="sb2">
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>
        <Stack direction="row" mx={2}>
          {currentPage > 1 && (
            <>
              <Button onClick={() => handlePageChange(1)}>1</Button>
              {currentPage > 2 && <Text>...</Text>}
            </>
          )}
          <Button backgroundColor="teal" color="white">
            {currentPage}
          </Button>
          {currentPage < totalPages - 1 && (
            <>
              <Text>...</Text>
              <Button onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </Button>
            </>
          )}
        </Stack>
        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      {/* Modal for Editing Category */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            {" "}
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Category
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <FormControl>
              <FormLabel>
                <Icon as={FaTags} mr={1} color="teal.500" />
                Category Name
              </FormLabel>
              <Input
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleUpdateCategory}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CategoryViewTable;
